import React, { createContext, useEffect, useState } from 'react';
import AdminAppBar from "../../Components/Admin/AdminAppBar";
import AdminAppSidebar from "./AdminAppSideBar";
import auriaLogo from "../../assets/img/AURIA_LOGO_WHITE+COLOR_64.png";
import Box from '@mui/material/Box';
import { getUser } from 'src/Services/BorgAPIClient';
import { User, Group as GroupType } from 'src/Constants/Types';
import Footer from '../Footer';

// Create context to share current logged in user data to all components within the admin container
interface AdminPageContextType {
  loggedInUser: User | null;
  selectedPage: string;
  adminGroupList: GroupType[];
  updateAdminGroupList: (groupList: GroupType[]) => void;
  // Maintain quiz context across pages
  currentQuizId: string | null;
  currentSessionId: string | null;
  setQuizContext: (quizId: string | null, sessionId: string | null) => void;
}

const LoggedInUserContext = createContext<AdminPageContextType | null>(null);

type Props = { children: React.ReactNode };

const AdminPageContainer = (props: Props) => {
  const [loggedInUser, setLoggedInUser] = useState<User | null>(null);
  const [selectedPage, setSelectedPage] = useState<string>("");
  const [adminGroupList, setAdminGroupList] = useState<GroupType[]>([]);
  const [currentQuizId, setCurrentQuizId] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  
  useEffect(() => {
    fetchUserData();
  }, []);

  // Sync context to localStorage (so it works for opening quizpage by sub buttons and in new tabs)
  useEffect(() => {
    syncQuizContextToLocalStorage();
  }, [currentQuizId, currentSessionId]);

  const fetchUserData = async () => {
    try {
      const userId = JSON.parse(localStorage.getItem('userId') as string);
      if (userId) {
        const user = await getUser(userId.replace(/"|'/g, ''));
        if (user) {
          setLoggedInUser(user.data);

          //set quiz context from localStorage if it exists. 
          // This is so if youre on the instructor page and you've drilled down to a quiz, on refresh the quiz will still be open
          const quizId = localStorage.getItem('quizId');
          const sessionId = localStorage.getItem('sessionId');
          if (quizId) {
            setCurrentQuizId(quizId);
          }
          if (sessionId) {
            setCurrentSessionId(sessionId);
          }
          
        }
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
  };
  
  const setQuizContext = (quizId: string | null, sessionId: string | null) => {
    setCurrentQuizId(quizId);
    setCurrentSessionId(sessionId);
  };

  const syncQuizContextToLocalStorage = () => {
    if (currentQuizId !== null) {
      localStorage.setItem('quizId', currentQuizId);
      console.debug('Synced quizId to localStorage:', currentQuizId);
    }
    if (currentSessionId !== null) {
      localStorage.setItem('sessionId', currentSessionId);
      console.debug('Synced sessionId to localStorage:', currentSessionId);
    }
  }

  const handlePageChange = (page: string) => {
    setSelectedPage(page);
  };
  
  const handleAdminGroupListChange = (groupList: GroupType[]) => {
    setAdminGroupList(groupList);
  };

  return (
    <LoggedInUserContext.Provider value={{
      loggedInUser,
      selectedPage,
      adminGroupList,
      updateAdminGroupList: handleAdminGroupListChange,
      currentQuizId,
      currentSessionId,
      setQuizContext
    }}>
      <Box className="admin-page-container">
        <AdminAppBar onSelectedPageChange={handlePageChange} />
        <Box className='admin-page-container-content'>
          {/* Sidebar */}
          <Box sx={{ flex: 1, bgcolor: 'background.paper', maxWidth: 'fit-content' }}>
            <AdminAppSidebar />
          </Box>

          {/* Main Content Area */}
          <Box className='admin-main-content' sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}>
            {/* Page Content */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'hidden',
              marginLeft: 2,
              marginRight: 2
            }}>
              {props.children}
            </Box>
            <Footer showAbout={false} />
          </Box>
        </Box>
      </Box>
    </LoggedInUserContext.Provider>
  );
};

export default AdminPageContainer;
export { LoggedInUserContext };